import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';

export interface DailyPayoutResponse extends Record<string, unknown> {
  daily_payout_amount?: number | string | null;
  currency_code?: string | null;
  currency_label?: string | null;
  currency?: string | null;
  daily_currency_code?: string | null;
  daily_currency_label?: string | null;
}

interface UseDailyPayoutQuoteParams {
  initiatorId?: number;
  tripType?: 'local' | 'international';
  startDate?: string | null;
  endDate?: string | null;
  enabled?: boolean;
}

export function useDailyPayoutQuote({
  initiatorId,
  tripType,
  startDate,
  endDate,
  enabled = true,
}: UseDailyPayoutQuoteParams) {
  return useQuery<DailyPayoutResponse, Error>({
    queryKey: [
      'trip-form',
      'daily-payout-request',
      initiatorId ?? null,
      tripType ?? null,
      startDate ?? null,
      endDate ?? null,
    ],
    enabled: Boolean(enabled && initiatorId && tripType && startDate && endDate),
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!initiatorId || !tripType || !startDate || !endDate) {
        throw new Error('Missing daily payout parameters');
      }

      const response = await apiFetch('/api/v1/business-trip-requests/daily-payout-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          initiator_id: initiatorId,
          trip_type: tripType,
          start_date: startDate,
          end_date: endDate,
        }),
      });

      const payload = (await response.json().catch(() => null)) as
        | DailyPayoutResponse
        | { error?: { message?: unknown } }
        | null;

      if (!response.ok || !payload || typeof payload !== 'object') {
        throw new Error('Failed to fetch daily payout details');
      }

      if ('error' in payload && payload.error && typeof payload.error === 'object') {
        const maybeMessage = (payload.error as { message?: unknown }).message;
        if (typeof maybeMessage === 'string' && maybeMessage.trim()) {
          throw new Error(maybeMessage.trim());
        }
        throw new Error('Failed to fetch daily payout details');
      }

      return payload as DailyPayoutResponse;
    },
  });
}
